import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { HeaderPaddingSafeAreaComponent } from '../../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import { SharedModule } from '../../@commons/ui-components/shared.module';
import { StorageService } from '../../@commons/@services/front/storage.service';
import { BedItemComponent } from '../@components/bed-item/bed-item.component';
import { ZoneCreateWizardComponent } from '../zone-create-wizard/zone-create-wizard.component';
import { UserAdressLocationFormComponent } from '../../user-profile/@components/user-address-card/@toasts/edit-user-adress-toast/user-adress-location-form/user-adress-location-form.component';
import { ConfirmDialogComponent } from '../../@commons/ui-components/confirm-dialog/confirm-dialog.component';
import { ActionNeededBannerComponent } from '../../@commons/ui-components/action-needed-banner/action-needed-banner.component';
import { CultivationItemViewerComponent } from '../@components/hierarchy-item/cultivation-item-viewer/cultivation-item-viewer.component';
import { SkeletonLoaderComponent } from '../../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { LocationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/location-api';
import { GardenBedApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/garden-bed/garden-bed-api';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { LocationListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/location-list.response';
import { GardenBedListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/garden-bed-list.response';
import { PlantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/plant-list.response';
import { bedSpace, coveredCells } from '../@services/garden-space.util';

interface BedView {
  bed: GardenBedListResponse;
  cultures: any[];
  count: number;
  /** Nombre total de plants (Σ nbPlants des cohortes). */
  plantCount: number;
  /** Place libre : cases qu'aucune emprise ne couvre (en cases), places (pot). */
  freeCells: number;
  totalCells: number;
  /** Cases couvertes par une emprise (x = colonne, y = ligne) du mini-aperçu. */
  occupied: { x: number; y: number }[];
  /** Au moins une plante attend une action (étape manuelle dont la durée est écoulée). */
  actionNeeded: boolean;
}

/** Cultures hors planche regroupées par culture (comme l'accueil) : une vignette + un compteur. */
interface CultureGroup {
  culture: any;
  cultivationUid?: string;
  /** Nombre d'enregistrements (cohortes) du groupe. */
  count: number;
  /** Nombre total de plants physiques du groupe = Σ nbPlants (affiché avec le logo feuille). */
  plantCount: number;
  plants: PlantListResponse[];
  /** Au moins une plante du groupe attend une action. */
  actionNeeded: boolean;
}

import { PremiumTeaserComponent } from '../../@commons/ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../../@commons/@toasts/premium-toast/premium-toast.component';
import { LockIconComponent } from '../../@icons/lock-icon.component';
import { PremiumService } from '../../@commons/@services/front/premium.service';
import { SeasonPlanBannerComponent } from '../../season-plan/season-plan-banner/season-plan-banner.component';
import { VwPluralPipe } from '../../@commons/pipes/vw-plural.pipe';
import { StateComponent } from '../../@commons/ui-components/state/state.component';
import { OfflineBannerComponent } from '../../@commons/ui-components/state/offline-banner.component';
import { DataStore, SNAPSHOT_ONLY } from '../../@commons/@services/data/data-store.service';
import { LOADING, LoadState, errorState } from '../../@commons/@services/data/load-state';
import { GardenStore } from '../../@commons/@services/data/garden.store';
import { stepNeedsAction } from '../../@commons/utils/step-clock.util';
import { withThermalProgress } from '../../@commons/utils/garden-todo.util';

/** Limites du plan gratuit, alignées sur `premium-limits.util.ts` du back. */
const FREE_MAX_ZONES = 2;
const FREE_MAX_BEDS_PER_ZONE = 3;

/** Nom de la zone créée d'office, en intérieur, par l'ancien onboarding. */
const DEFAULT_ZONE_NAME = 'ZONE 1';
/** Cultures citées dans la question sur la zone d'office. */
const INDOOR_CHECK_MAX_NAMES = 3;

interface ZoneView {
  zone: LocationListResponse;
  beds: BedView[];
  looseCultures: CultureGroup[];
  /** Zone au-delà du palier gratuit : consultable, plus de nouvelle plantation. */
  locked?: boolean;
}

/** Zone d'office « ZONE 1 » en intérieur qui porte des plantes : à faire confirmer. */
interface IndoorCheck {
  view: ZoneView;
  /** Noms des cultures de la zone, pour que la question parle de SES plantes. */
  plantNames: string;
}

import { EmptyStateComponent } from '../../@commons/ui-components/empty-state/empty-state.component';
@Component({
  selector: 'vw-garden-overview',
  imports: [
    SharedModule,
    HeaderPaddingSafeAreaComponent,
    CommonModule,
    TranslatePipe,
    BedItemComponent,
    CultivationItemViewerComponent,
    SkeletonLoaderComponent,
    StateComponent,
    OfflineBannerComponent,
    ZoneCreateWizardComponent,
    UserAdressLocationFormComponent,
    ConfirmDialogComponent,
    ActionNeededBannerComponent,
    PremiumTeaserComponent,
    PremiumToastComponent,
    LockIconComponent,
    SeasonPlanBannerComponent,
    VwPluralPipe,
    EmptyStateComponent,
  ],
  templateUrl: './garden-overview.component.html',
  styleUrl: './garden-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: LocationApi, useClass: LocationApi },
    { provide: GardenBedApi, useClass: GardenBedApi },
    { provide: PlantApi, useClass: PlantApi },
  ],
})
export class GardenOverviewComponent implements OnInit {
  /**
   * Chargement du potager. Une panne (réseau, 429…) s'affiche avec
   * « Réessayer », jamais « potager vide » qui mentirait sur les données de
   * l'utilisateur (145).
   */
  state: LoadState = LOADING;
  zones: ZoneView[] = [];

  readonly premiumService = inject(PremiumService);
  private readonly gardenStore = inject(GardenStore);
  private readonly dataStore = inject(DataStore);

  /**
   * Structures verrouillées après un retour au palier gratuit.
   *
   * Lues via `downgrade-summary`, qui les calcule déjà : ajouter un flag
   * `locked` aux DTO de liste imposerait un contrôle d'abonnement à chaque
   * appel de liste, partout dans l'app, pour un besoin propre à cet écran.
   *
   * Rien n'est supprimé — ces espaces restent consultables et leurs cultures
   * en cours pleinement gérables. Seule une NOUVELLE plantation est réservée.
   */
  private lockedZoneUids = new Set<string>();
  private lockedBedUids = new Set<string>();
  premiumPaywallOpen = false;
  /** D'où vient l'ouverture, pour savoir quelle limite convertit. */
  paywallSource = 'garden_overview';

  /**
   * Un seul encart, en bas de l'inventaire, et seulement une fois la limite
   * atteinte — pas avant. Il nomme la limite RÉELLEMENT rencontrée : quelqu'un
   * qui a rempli une planche ne veut pas s'entendre parler de zones.
   *
   * `null` quand rien n'est limitant : l'encart disparaît.
   */
  get structureLimitHit(): 'beds' | 'zones' | null {
    if (!this.premiumService.ready()) return null;
    if (this.premiumService.hasPremiumAccess()) return null;
    if (this.zones.some((z) => z.beds.length >= FREE_MAX_BEDS_PER_ZONE)) {
      return 'beds';
    }
    return this.zones.length >= FREE_MAX_ZONES ? 'zones' : null;
  }
  orphanCultures: CultureGroup[] = [];

  /**
   * L'ancien onboarding créait d'office « ZONE 1 » en intérieur, et l'intérieur
   * coupe les alertes de gel, de mildiou ou de limaces. Quand cette zone porte
   * des plantes, on demande si elle est vraiment à l'intérieur.
   *
   * La réponse se lit dans la zone elle-même : « Oui » la renomme du nom de son
   * type (comme une zone créée aujourd'hui), « Non » change son type. Dans les
   * deux cas elle n'est plus une zone d'office et la question ne revient pas,
   * sur aucun appareil.
   */
  indoorCheck: IndoorCheck | null = null;
  confirmingIndoor = false;

  showZoneForm = false;
  /** Une demande d'ajout de zone est arrivée par l'URL. */
  private zoneFormRequested = false;

  // Édition / suppression de zone (slide-in du formulaire + dialogue de confirmation).
  showZoneEdit = false;
  editZone?: any;
  private editZoneView?: ZoneView;
  showZoneDeleteConfirm = false;
  deletingZone = false;
  /** Suppression de zone refusée : dit dans la confirmation. */
  zoneDeleteFailed = false;
  zoneDeleteCount = 0;
  private deleteTargetUid?: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private locationApi: LocationApi,
    private gardenBedApi: GardenBedApi,
    private plantApi: PlantApi,
    private translate: TranslateService,
  ) {}

  async ngOnInit(): Promise<void> {
    // Le toast « + » → « Ajouter une zone » navigue ici avec ?addZone=1 pour ouvrir le formulaire.
    this.route.queryParamMap.subscribe((params) => {
      this.zoneFormRequested = params.get('addZone') === '1';
      // Les zones ne sont pas encore chargées au premier passage : la décision
      // se prend après `reload()`, sinon le compteur vaut zéro et la limite
      // n'est jamais vue.
      if (this.state.status !== 'loading') this.resolveZoneFormRequest();
    });
    await this.reload();
    if (this.state.status !== 'error') this.resolveZoneFormRequest();
  }

  /** État vide : la première plante, par l'assistant d'ajout. */
  plantFirst(): void {
    void this.router.navigate(['/garden/add-plant']);
  }

  /** État vide : la première zone, par l'assistant de zone (sous la même règle que le « + »). */
  addFirstZone(): void {
    this.zoneFormRequested = true;
    this.resolveZoneFormRequest();
  }

  /** Bouton « Réessayer » de l'état d'erreur. */
  async retry(): Promise<void> {
    await this.reload();
    if (this.state.status !== 'error') this.resolveZoneFormRequest();
  }

  /**
   * Soft gate POSITIF sur l'ajout de zone : le « + » n'échoue jamais.
   *
   * À la limite, l'invitation s'ouvre au lieu du formulaire — l'utilisateur ne
   * remplit pas un formulaire pour se faire refuser à l'enregistrement.
   */
  private resolveZoneFormRequest(): void {
    if (!this.zoneFormRequested) {
      this.showZoneForm = false;
      return;
    }
    if (!this.premiumService.hasPremiumAccess() && this.zones.length >= FREE_MAX_ZONES) {
      this.zoneFormRequested = false;
      this.showZoneForm = false;
      this.paywallSource = 'zone_limit';
      this.premiumPaywallOpen = true;
      this.clearAddZoneParam();
      return;
    }
    this.showZoneForm = true;
  }

  private clearAddZoneParam(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { addZone: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private async reload(): Promise<void> {
    this.state = LOADING;
    try {
      // En parallèle : les espaces verrouillés ne dépendent pas du jardin.
      await Promise.all([this.loadGarden(), this.loadLockedStructures()]);
      // Le verrou est posé une fois les DEUX réponses arrivées, quel que soit leur ordre.
      for (const view of this.zones) view.locked = this.isZoneLocked(view.zone.uid);
      // L'écran a son propre « potager vide » (aucune zone, aucune culture).
      this.state = { status: 'ready' };
    } catch (error) {
      this.state = errorState(error);
    }
  }

  /** Fermeture du formulaire de zone : on efface le signal d'URL et on recharge si une zone a été créée. */
  async onZoneFormClosed(location?: any): Promise<void> {
    this.showZoneForm = false;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { addZone: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    if (location) {
      await this.reload();
    }
  }

  private async loadGarden(): Promise<void> {
    const lang = this.storageService.getCurrentLanguage();
    const addressUid = this.storageService.getItem('address-uid') ?? undefined;

    // Zones et cultures passent par le store : l'onglet « Plantations (N) » et
    // les cartes d'adresse du profil lisent les mêmes. Tout est gardé pour
    // ouvrir Mon potager sans réseau (NA-05).
    const [zones, bedsRes, plantsRes, hierarchies, thermalRes] = await Promise.all([
      this.gardenStore.zones(addressUid),
      this.dataStore.get(`garden/beds:${lang}`, () => this.gardenBedApi.list({} as any, 0, 200, lang), SNAPSHOT_ONLY),
      this.dataStore.get(`garden/plants:${lang}`, () => this.plantApi.list({} as any, 0, 500, lang), SNAPSHOT_ONLY),
      this.gardenStore.myGarden(),
      // La progression thermique, lue aussi par l'accueil : « action attendue »
      // suit la même horloge que la fiche. Sans elle, repli sur le calendrier.
      this.dataStore
        .get('garden/thermal-progress', () => this.plantApi.getThermalProgressBatch(), SNAPSHOT_ONLY)
        .catch(() => null),
    ]);

    const beds = bedsRes.results ?? [];
    const plants = withThermalProgress(plantsRes.results ?? [], thermalRes);

    // Carte cultivationUid → culture « riche » (image + nom court) issue de myGarden.
    const cultureByUid = new Map<string, any>();
    for (const h of (hierarchies as any[]) ?? []) {
      for (const c of h.cultivations ?? []) cultureByUid.set(c.uid, c);
    }
    const culturesByBed = new Map<string, any[]>();
    const plantCountByBed = new Map<string, number>();
    const actionNeededByBed = new Map<string, boolean>();
    const looseByZone = new Map<string, PlantListResponse[]>();
    const orphanPlants: PlantListResponse[] = [];
    const zoneUids = new Set(zones.map((z) => z.uid));

    for (const p of plants) {
      const bedUid = (p as any).gardenBedUid;
      if (bedUid) {
        this.push(
          culturesByBed,
          bedUid,
          cultureByUid.get((p as any).cultivationUid) ?? p.cultivationVariant,
        );
        // Plants comptés par cohorte (nbPlants), pas par enregistrement.
        plantCountByBed.set(
          bedUid,
          (plantCountByBed.get(bedUid) ?? 0) + ((p as any).nbPlants ?? 1),
        );
        if (stepNeedsAction(p)) actionNeededByBed.set(bedUid, true);
      } else if (p.location?.uid && zoneUids.has(p.location.uid)) {
        this.push(looseByZone, p.location.uid, p);
      } else {
        orphanPlants.push(p);
      }
    }

    this.orphanCultures = this.groupByCultivation(orphanPlants, cultureByUid);

    const bedsByZone = new Map<string, GardenBedListResponse[]>();
    for (const b of beds) if (b.locationUid) this.push(bedsByZone, b.locationUid, b);

    this.zones = zones.map((zone) => ({
      zone,
      beds: (bedsByZone.get(zone.uid) ?? []).map((bed) => {
        const cultures = culturesByBed.get(bed.uid) ?? [];
        const plantCount = plantCountByBed.get(bed.uid) ?? 0;
        // Place libre du back (la même que sur le plan) ; les cases couvertes colorent le mini-aperçu.
        const space = bedSpace(bed);
        return {
          bed,
          cultures,
          count: cultures.length,
          plantCount,
          freeCells: space.free,
          totalCells: space.total,
          occupied: coveredCells(bed, plants),
          actionNeeded: actionNeededByBed.get(bed.uid) ?? false,
        };
      }),
      looseCultures: this.groupByCultivation(
        looseByZone.get(zone.uid) ?? [],
        cultureByUid,
      ),
    }));
    this.indoorCheck = this.findIndoorCheck();
  }

  /** Première zone d'office en intérieur qui porte des plantes, sinon `null`. */
  private findIndoorCheck(): IndoorCheck | null {
    for (const view of this.zones) {
      const zone = view.zone;
      if (zone.locationType !== 'INDOOR') continue;
      if (zone.name?.trim().toUpperCase() !== DEFAULT_ZONE_NAME) continue;
      const cultures = [
        ...view.beds.flatMap((b) => b.cultures),
        ...view.looseCultures.map((g) => g.culture),
      ];
      if (!cultures.length) continue;
      const names = [...new Set(cultures.map((c) => c?.name).filter(Boolean))];
      return {
        view,
        plantNames: names.slice(0, INDOOR_CHECK_MAX_NAMES).join(', '),
      };
    }
    return null;
  }

  /**
   * « Oui, à l'intérieur » : la zone prend le nom de son type. Le bandeau part
   * tout de suite ; il revient si l'enregistrement échoue, pour que la réponse
   * ne se perde pas.
   */
  async confirmIndoor(): Promise<void> {
    const check = this.indoorCheck;
    if (!check || this.confirmingIndoor) return;
    this.confirmingIndoor = true;
    this.indoorCheck = null;
    const name = this.translate.instant('common.location-types.INDOOR');
    try {
      await this.locationApi.update(
        { uid: check.view.zone.uid, name } as any,
        this.storageService.getCurrentLanguage(),
      );
      check.view.zone.name = name;
    } catch (error) {
      console.warn('GardenOverview: failed to save the indoor answer', error);
      this.indoorCheck = check;
    } finally {
      this.confirmingIndoor = false;
    }
  }

  /** « Non, c'est dehors » : le formulaire de la zone, où l'on change son type. */
  declareOutdoor(): void {
    if (this.indoorCheck) this.openZoneEdit(this.indoorCheck.view);
  }

  /** Regroupe des plantes par culture (comme l'accueil) : une entrée par culture + son nombre de plants. */
  private groupByCultivation(
    plants: PlantListResponse[],
    cultureByUid: Map<string, any>,
  ): CultureGroup[] {
    const byKey = new Map<string, CultureGroup>();
    for (const p of plants) {
      const cultivationUid = (p as any).cultivationUid;
      const key = cultivationUid ?? p.cultivationVariant?.uid ?? p.uid;
      let group = byKey.get(key);
      if (!group) {
        group = {
          culture: cultureByUid.get(cultivationUid) ?? p.cultivationVariant,
          cultivationUid,
          count: 0,
          plantCount: 0,
          plants: [],
          actionNeeded: false,
        };
        byKey.set(key, group);
      }
      group.plants.push(p);
      group.count++;
      group.plantCount += (p as any).nbPlants ?? 1;
      if (stepNeedsAction(p)) group.actionNeeded = true;
    }
    return Array.from(byKey.values());
  }

  private push<T>(map: Map<string, T[]>, key: string, value: T): void {
    const list = map.get(key) ?? [];
    list.push(value);
    map.set(key, list);
  }

  openBed(bed: GardenBedListResponse): void {
    this.router.navigate(['/garden/beds', bed.uid]);
  }

  /**
   * Ouvre une culture hors planche regroupée (tap sur la vignette) → liste de ses plants.
   * Sur « Mon Potager » l'affichage est groupé par zone : on restreint donc la liste aux plants
   * de la zone cliquée (les plants d'un groupe partagent la même zone). Les cultures orphelines
   * (sans zone) restent non filtrées, comme sur l'accueil.
   */
  openCulture(group: CultureGroup): void {
    if (group?.cultivationUid) {
      const locationUid = group.plants?.[0]?.location?.uid;
      this.router.navigate(
        ['/garden/cultivations', group.cultivationUid, 'plants'],
        locationUid ? { queryParams: { locationUid } } : {},
      );
    } else {
      this.openPlant(group?.plants?.[0]);
    }
  }

  /** Ouvre le détail d'une plante précise (fallback quand la culture n'a pas d'uid de cultivation). */
  openPlant(plant: any): void {
    const cultivationUid = plant?.cultivationUid;
    const variantUid = plant?.cultivationVariant?.uid;
    if (cultivationUid && variantUid && plant?.uid) {
      this.router.navigate([
        '/garden/cultivations',
        cultivationUid,
        'variants',
        variantUid,
        'plants',
        plant.uid,
      ]);
    }
  }

  /**
   * Soft gate POSITIF : le « + » n'échoue jamais.
   *
   * Le front connaît les limites du plan gratuit, il ouvre donc l'invitation
   * AVANT d'appeler le serveur. Laisser partir la requête pour se faire
   * refuser fait vivre un échec là où il n'y a qu'une offre à découvrir — et
   * consomme un aller-retour réseau pour rien.
   *
   * Le serveur reste seul juge de l'autorisation : ce garde-fou est un confort
   * d'interface, pas une sécurité.
   */
  addBedToZone(zone: LocationListResponse): void {
    const view = this.zones.find((z) => z.zone.uid === zone.uid);
    if (this.bedLimitReached(view)) {
      this.paywallSource = 'bed_limit';
      this.premiumPaywallOpen = true;
      return;
    }
    this.router.navigate(['/garden/beds/create'], {
      queryParams: { locationUid: zone.uid },
    });
  }

  /** Depuis l'encart de bas de page : la source dit quelle limite convertit. */
  openLimitPaywall(): void {
    this.paywallSource =
      this.structureLimitHit === 'beds' ? 'bed_limit' : 'zone_limit';
    this.premiumPaywallOpen = true;
  }

  isZoneLocked(uid?: string): boolean {
    return Boolean(uid && this.lockedZoneUids.has(uid));
  }

  isBedLocked(uid?: string): boolean {
    return Boolean(uid && this.lockedBedUids.has(uid));
  }

  /** Y a-t-il au moins un espace verrouillé à expliquer ? */
  get hasLockedStructures(): boolean {
    return this.lockedZoneUids.size > 0 || this.lockedBedUids.size > 0;
  }

  /** Le « + » d'une zone verrouillée ouvre l'offre, il ne plante rien. */
  openLockedStructurePaywall(): void {
    this.paywallSource = 'locked_structure';
    this.premiumPaywallOpen = true;
  }

  /**
   * Charge les espaces verrouillés. Inutile pour un abonné : rien ne l'est.
   * Un échec laisse simplement l'écran sans badges — jamais d'erreur affichée
   * pour une information d'appoint.
   */
  private async loadLockedStructures(): Promise<void> {
    this.lockedZoneUids = new Set();
    this.lockedBedUids = new Set();
    if (this.premiumService.hasPremiumAccess()) return;
    try {
      const summary = await this.locationApi.getDowngradeSummary();
      this.lockedZoneUids = new Set(
        (summary?.lockedZones ?? []).map((z) => z.uid),
      );
      this.lockedBedUids = new Set((summary?.lockedBeds ?? []).map((b) => b.uid));
    } catch {
      // Information d'appoint : son absence ne doit rien casser.
    }
  }

  /** Cette zone a-t-elle atteint le plafond gratuit de planches ? */
  bedLimitReached(view?: ZoneView): boolean {
    if (this.premiumService.hasPremiumAccess() || !view) return false;
    return view.beds.length >= FREE_MAX_BEDS_PER_ZONE;
  }

  /** Emoji illustratif du type de zone (aligné sur le sélecteur de type). */
  zoneEmoji(type?: string): string {
    switch (type) {
      case 'GREENHOUSE':
        return '🏡';
      case 'BALCONY':
        return '🪟';
      case 'TERRACE':
        return '☀️';
      case 'INDOOR':
        return '🛋️';
      case 'PLANTER_BOX':
        return '🪴';
      case 'GARDEN':
      default:
        return '🌱';
    }
  }

  // --- Édition / suppression d'une zone (point d'entrée : tap sur l'en-tête de zone) ---
  openZoneEdit(z: ZoneView): void {
    this.editZoneView = z;
    this.editZone = z.zone;
    this.showZoneEdit = true;
  }

  onZoneEditClosed(location?: any): void {
    this.showZoneEdit = false;
    this.editZone = undefined;
    if (location) {
      this.reload();
    }
  }

  onZoneDeleteRequest(): void {
    const z = this.editZoneView;
    this.showZoneEdit = false;
    if (!z) return;
    this.deleteTargetUid = z.zone.uid;
    this.zoneDeleteCount = this.zonePlantCount(z);
    this.zoneDeleteFailed = false;
    this.showZoneDeleteConfirm = true;
  }

  cancelZoneDelete(): void {
    if (this.deletingZone) return;
    this.showZoneDeleteConfirm = false;
  }

  async confirmZoneDelete(): Promise<void> {
    if (!this.deleteTargetUid || this.deletingZone) return;
    this.deletingZone = true;
    this.zoneDeleteFailed = false;
    try {
      await this.locationApi.deleteById(
        this.deleteTargetUid,
        this.storageService.getCurrentLanguage(),
      );
      this.showZoneDeleteConfirm = false;
      await this.reload();
    } catch (e) {
      // Suppression refusée : la confirmation reste ouverte et le dit, on peut
      // réessayer. (Zone supprimée mais rechargement raté : rien à signaler ici.)
      console.error('GardenOverviewComponent: failed to delete zone', e);
      this.zoneDeleteFailed = this.showZoneDeleteConfirm;
    } finally {
      this.deletingZone = false;
    }
  }

  /** Nombre de plants actifs d'une zone (toutes planches + hors planche). */
  zonePlantCount(z: ZoneView): number {
    const inBeds = z.beds.reduce((sum, b) => sum + (b.count || 0), 0);
    const loose = (z.looseCultures || []).reduce((sum, g) => sum + (g.count || 0), 0);
    return inBeds + loose;
  }
}
